import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import LanguageContext from '../contexts/LanguageContext';
import { getTranslation } from '../services/languageService';
import Markdown from '../components/Markdown';
import './About.scss';

const About = () => {
  const { language } = useContext(LanguageContext);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Core values data
  const coreValues = [
    {
      title: getTranslation('about.values.innovation', language),
      description: "We develop innovative solutions that make advanced technology accessible and affordable for SMEs."
    },
    {
      title: getTranslation('about.values.integrity', language),
      description: "We maintain the highest ethical standards and transparency in all our dealings with SME clients."
    },
    {
      title: getTranslation('about.values.excellence', language),
      description: "We deliver exceptional results tailored to the unique needs and budgets of Malaysian SMEs."
    },
    {
      title: getTranslation('about.values.collaboration', language),
      description: "We work closely with SMEs as partners, understanding their challenges and growing together."
    }
  ];

  // Team members data
  const teamMembers = getTranslation('about.team.members', language) || [];

  return (
    <motion.div
      className="about"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <section className="section">
        <div className="container">
          <motion.h1 className="page-title text-center" variants={itemVariants}>
            {getTranslation('about.title', language)}
          </motion.h1>
          
          <div className="about-content">
            <motion.div className="about-section" variants={itemVariants}>
              <p>{getTranslation('about.intro', language)}</p>
            </motion.div>

            <motion.div className="about-section" variants={itemVariants}>
              <h2 className="section-title">{getTranslation('about.vision.title', language)}</h2>
              <p>{getTranslation('about.vision.description', language)}</p>
            </motion.div>
            
            <motion.div className="about-section" variants={itemVariants}>
              <h2 className="section-title">{getTranslation('about.values.title', language)}</h2>
              <div className="values-grid">
                {coreValues.map((value, index) => (
                  <div key={index} className="value-card card">
                    <h3 className="value-title">{value.title}</h3>
                    <Markdown className="value-description">{value.description}</Markdown>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div className="about-section team-section" variants={itemVariants}>
              <h2 className="section-title">{getTranslation('about.team.title', language)}</h2>
              <div className="team-grid">
                {teamMembers.map((member, index) => (
                  <div key={index} className="team-member-card card">
                    <div className="member-photo">
                      {member.photo ? (
                        <img src={member.photo} alt={member.name} className="member-image" />
                      ) : (
                        <div className="photo-placeholder">
                          <span className="member-initials">
                            {member.name.split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="member-info">
                      <h3 className="member-name">{member.name}</h3>
                      <p className="member-position">{member.position}</p>
                      <p className="member-bio">{member.bio}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default About;
